import { Divider, Grid2, TextField, Typography } from "@mui/material";
import { useEffect } from "react";
import {
	Control,
	Controller,
	UseFormSetValue,
	useWatch,
} from "react-hook-form";
import { TolerancePicker } from "@/controls/TolerancePicker";
import { Product } from "@/shared/models/Product";
import { ClipSelector } from "@/shared/selectors/ClipSelector";
import { KeeperSelector } from "@/shared/selectors/KeeperSelector";
import { Clip } from "@/shared/models/Clip";
import { parseNumberInput } from "@/shared/utils/parseNumberInput";
import { toFraction32 } from "@/utils/fraction";

type Props = {
	control: Control<Product>;
	setValue: UseFormSetValue<Product>;
	disabled: boolean;
	selectedClip: Clip | null;
	onClipChange: (clipId: number | null, clip: Clip | null) => void;
	parentSawCutLength: number | null;
};

const SectionHeader = ({ title }: { title: string }) => (
	<Grid2 size={{ xs: 12 }}>
		<Typography
			variant="subtitle2"
			fontWeight={600}
			color="primary"
			sx={{ mb: 0.5, mt: 1 }}
		>
			{title}
		</Typography>
		<Divider />
	</Grid2>
);

export const WeldSpecsFields = ({
	control,
	setValue,
	disabled,
	selectedClip,
	onClipChange,
	parentSawCutLength,
}: Props) => {
	const bundleCol = useWatch({ control, name: "BundleCol" });
	const bundleRow = useWatch({ control, name: "BundleRow" });

	useEffect(() => {
		const col = bundleCol ?? 0;
		const row = bundleRow ?? 0;
		const count = col && row ? col * row : null;
		setValue("BundleCount", count, { shouldDirty: true });
	}, [bundleCol, bundleRow, setValue]);

	return (
		<>
			{/* Clip */}
			<SectionHeader title="Clip" />
			<Grid2 size={{ xs: 12, md: 3.8 }}>
				<Controller
					name="ClipId"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<ClipSelector
							label="Select Clip"
							value={field.value}
							onChange={(clipId, clip) => {
								field.onChange(clipId);
								onClipChange(clipId, clip);
							}}
							fieldError={error}
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
			{selectedClip && (
				<>
					<Grid2 size={{ xs: 3, md: 1.2 }}>
						<TextField
							label="Width"
							value={selectedClip.Width}
							fullWidth
							size="small"
							disabled
						/>
					</Grid2>
					<Grid2 size={{ xs: 3, md: 1.2 }}>
						<TextField
							label="Height"
							value={selectedClip.Height}
							fullWidth
							size="small"
							disabled
						/>
					</Grid2>
					<Grid2 size={{ xs: 6, md: 2 }}>
						<TextField
							label="Hole Pattern"
							value={selectedClip.HolePattern || ""}
							fullWidth
							size="small"
							disabled
						/>
					</Grid2>
				</>
			)}

			{/* Weld Configuration */}
			<SectionHeader title="Weld Configuration" />
			<Grid2 size={{ xs: 12, md: 7 }}>
				<Controller
					name="Tolerance"
					control={control}
					render={({ field }) => (
						<TolerancePicker
							value={field.value}
							onChange={field.onChange}
							disabled={disabled}
							middle={
								<Controller
									name="FinishedLength"
									control={control}
									render={({ field: lengthField }) => (
										<TextField
											label="Finished Length"
											type="number"
											value={lengthField.value ?? ""}
											onChange={(e) =>
												lengthField.onChange(
													parseNumberInput(e.target.value, { min: 0 }),
												)
											}
											onBlur={() => {
												lengthField.onBlur();
												if (typeof lengthField.value === "number") {
													const r = Math.round(lengthField.value * 1000) / 1000;
													if (r !== lengthField.value) lengthField.onChange(r);
												}
											}}
											fullWidth
											size="small"
											disabled={disabled}
											helperText={
												typeof lengthField.value === "number"
													? toFraction32(lengthField.value)
													: undefined
											}
											inputProps={{ min: 0, step: 0.001 }}
										/>
									)}
								/>
							}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<TextField
					label="Cut Length"
					value={parentSawCutLength ?? ""}
					fullWidth
					size="small"
					disabled
					helperText={
						parentSawCutLength != null
							? `From Saw · ${toFraction32(parentSawCutLength)}`
							: "From Saw"
					}
					InputProps={{ readOnly: true }}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="Program"
					control={control}
					render={({ field }) => (
						<TextField
							label="Program"
							value={field.value || ""}
							onChange={field.onChange}
							fullWidth
							size="small"
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="CleaningCycle"
					control={control}
					render={({ field }) => (
						<TextField
							label="Clean Cycle"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(
									parseNumberInput(e.target.value, { integer: true, min: 0 }),
								)
							}
							fullWidth
							size="small"
							disabled={disabled}
							inputProps={{ min: 0, step: 1 }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="EntryKeeperId"
					control={control}
					render={({ field }) => (
						<KeeperSelector
							label="Entry Keeper"
							value={field.value}
							onChange={field.onChange}
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="ExitKeeperId"
					control={control}
					render={({ field }) => (
						<KeeperSelector
							label="Exit Keeper"
							value={field.value}
							onChange={field.onChange}
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="BottomKeeper"
					control={control}
					render={({ field }) => (
						<TextField
							label="Bottom Keeper"
							value={field.value || ""}
							onChange={field.onChange}
							fullWidth
							size="small"
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="SideGage"
					control={control}
					render={({ field }) => (
						<TextField
							label="Side Gage"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="HoleGage"
					control={control}
					render={({ field }) => (
						<TextField
							label="Hole Gage"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="TestCycleInterval"
					control={control}
					render={({ field }) => (
						<TextField
							label="Test Interval"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(
									parseNumberInput(e.target.value, { integer: true, min: 0 }),
								)
							}
							fullWidth
							size="small"
							disabled={disabled}
							inputProps={{ min: 0, step: 1 }}
						/>
					)}
				/>
			</Grid2>

			{/* Bundle Configuration */}
			<SectionHeader title="Bundle Configuration" />
			<Grid2 size={{ xs: 4, md: 1.7 }}>
				<Controller
					name="BundleCol"
					control={control}
					render={({ field }) => (
						<TextField
							label="Columns"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(
									parseNumberInput(e.target.value, { integer: true, min: 1 }),
								)
							}
							fullWidth
							size="small"
							disabled={disabled}
							inputProps={{ min: 1, step: 1 }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 4, md: 1.7 }}>
				<Controller
					name="BundleRow"
					control={control}
					render={({ field }) => (
						<TextField
							label="Rows"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(
									parseNumberInput(e.target.value, { integer: true, min: 1 }),
								)
							}
							fullWidth
							size="small"
							disabled={disabled}
							inputProps={{ min: 1, step: 1 }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 4, md: 2 }}>
				<Controller
					name="BundleCount"
					control={control}
					render={({ field }) => (
						<TextField
							label="Count"
							type="number"
							value={field.value ?? ""}
							fullWidth
							size="small"
							disabled
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 4, md: 2.5 }}>
				<Controller
					name="StickSize"
					control={control}
					render={({ field }) => (
						<TextField
							label="Stick Size"
							value={field.value || ""}
							onChange={field.onChange}
							fullWidth
							size="small"
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
		</>
	);
};
